import { GadgetSystemDominionId } from "../../database/DominionLiterals";
import { UniqueValidationId } from "../../database/validations/keys";
import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCartIdKey,
  ShopifyCartKey,
  ShopifyCartLineItemKey,
  ShopifyCartLineItemToCartKey,
  ShopifyCartToShopKey,
  ShopifyCartUniquenessValidationKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCartKey,
  defaultSearchableApiIdentifiers: ["note"],
  defaultFilterableApiIdentifiers: ["token"],
  "2022-01": {
    name: "Shopify Cart",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        key: ShopifyCartIdKey,
        dominionID: GadgetSystemDominionId,
      },
      {
        name: "Token",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "token" } },
        validations: {
          [ShopifyCartUniquenessValidationKey]: {
            key: ShopifyCartUniquenessValidationKey,
            specID: UniqueValidationId,
            configuration: {
              values: {
                scopeByField: ShopifyCartToShopKey,
              },
            },
          },
        },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "note" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "created_at" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Line Items",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "line_items" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCartLineItemKey,
          inverseFieldKey: ShopifyCartLineItemToCartKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCartToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "webhook_only" } as const,
  },
  "2025-04": {
    fields: [
      {
        name: "Token",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "token", availableInWebhook: ["carts/update", "carts/create"] },
        },
        validations: {
          [ShopifyCartUniquenessValidationKey]: {
            key: ShopifyCartUniquenessValidationKey,
            specID: UniqueValidationId,
            configuration: {
              values: {
                scopeByField: ShopifyCartToShopKey,
              },
            },
          },
        },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "note", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "created_at", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "updated_at", availableInWebhook: ["carts/update", "carts/create"] },
        },
      },
      {
        name: "Line Items",
        fieldType: FieldType.HasMany,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "line_items", availableInWebhook: ["carts/update", "carts/create"] },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCartLineItemKey,
          inverseFieldKey: ShopifyCartLineItemToCartKey,
        },
      },
    ],
  },
};

export const ShopifyCart = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
